import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Users, Loader2, AlertCircle } from 'lucide-react';
import Modal from '../ui/Modal';
import CompanionPlantPreview from './CompanionPlantPreview';
import { useCompanionPlants } from '../../hooks/useCompanionPlants';
import { useAuth } from '../../hooks/useAuth';
import { supabase, retryOperation } from '../../lib/supabase';

interface GardenSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  plantName: string;
  plantId: string;
}

export default function GardenSuccessModal({ 
  isOpen, 
  onClose, 
  plantName,
  plantId 
}: GardenSuccessModalProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { companions = [], loading, error, retry } = useCompanionPlants(plantId);
  const [gardenPlantIds, setGardenPlantIds] = useState<Set<string>>(new Set());
  const [fetchingGarden, setFetchingGarden] = useState(false);
  const [gardenError, setGardenError] = useState<string | null>(null);

  // Fetch user's garden plants with retry mechanism
  useEffect(() => {
    async function fetchGardenPlants() {
      if (!user || !isOpen) return;

      setFetchingGarden(true);
      setGardenError(null);

      try {
        const { data, error } = await retryOperation(
          () => supabase
            .from('user_gardens')
            .select('plant_id')
            .eq('user_id', user.id),
          3, // max retries
          1000 // initial delay
        );

        if (error) throw error;
        setGardenPlantIds(new Set(data?.map(item => item.plant_id) || []));
      } catch (err) {
        console.error('Error fetching garden plants:', err);
        setGardenError('Failed to load garden plants. Please try again.');
      } finally {
        setFetchingGarden(false);
      }
    }

    fetchGardenPlants();
  }, [user, isOpen]);

  // Retry fetching garden plants
  const handleRetryGardenFetch = () => {
    if (!user) return;
    
    setFetchingGarden(true);
    setGardenError(null);
    
    retryOperation(
      () => supabase
        .from('user_gardens')
        .select('plant_id')
        .eq('user_id', user.id),
      3, // max retries
      1000 // initial delay
    )
      .then(({ data, error }) => {
        if (error) throw error;
        setGardenPlantIds(new Set(data?.map(item => item.plant_id) || []));
      })
      .catch(err => {
        console.error('Error retrying garden plants fetch:', err);
        setGardenError('Failed to load garden plants. Please try again.');
      })
      .finally(() => {
        setFetchingGarden(false);
      });
  };

  // Filter out companions that are already in the garden and have valid data
  const availableCompanions = companions.filter(companion => {
    const companionPlant = companion.companion_plant || companion.plant;
    if (!companionPlant?.id) return false;
    return !gardenPlantIds.has(companionPlant.id);
  });

  const handleGoToGarden = () => {
    onClose();
    navigate('/my-garden');
  };

  const handleViewCompanions = () => {
    onClose();
    navigate(`/plants/${plantId}?tab=companions`, { state: { scrollToCompanions: true } });
  };

  const handleCompanionClick = (companionId: string) => {
    onClose();
    navigate(`/plants/${companionId}`);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="">
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-green-100 rounded-full">
            <Check className="h-8 w-8 text-green-600" />
          </div>
        </div>
        <h3 className="text-xl font-semibold mb-2">
          {plantName} successfully added to garden!
        </h3>

        {loading || fetchingGarden ? (
          <div className="mt-6 mb-8 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
          </div>
        ) : error ? (
          <div className="mt-6 mb-8">
            <div className="flex items-center justify-center gap-2 text-red-600 mb-4">
              <AlertCircle className="h-5 w-5" />
              <p>Error loading companion plants</p>
            </div>
            <button
              onClick={retry}
              className="text-primary-600 hover:text-primary-700 underline"
            >
              Try Again
            </button>
          </div>
        ) : gardenError ? (
          <div className="mt-6 mb-8">
            <div className="flex items-center justify-center gap-2 text-red-600 mb-4">
              <AlertCircle className="h-5 w-5" />
              <p>{gardenError}</p>
            </div>
            <button
              onClick={handleRetryGardenFetch}
              className="text-primary-600 hover:text-primary-700 underline"
            >
              Try Again
            </button>
          </div>
        ) : availableCompanions.length > 0 ? (
          <div className="mt-6 mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Users className="h-5 w-5 text-primary-500" />
              <h4 className="font-medium">Recommended Companion Plants</h4>
            </div>
            <div className="space-y-2">
              {availableCompanions.slice(0, 3).map((companion) => {
                const companionPlant = companion.companion_plant || companion.plant;
                return (
                  <div
                    key={companion.id}
                    onClick={() => handleCompanionClick(companionPlant.id)}
                    className="cursor-pointer"
                  >
                    <CompanionPlantPreview
                      companion={companion}
                      onClose={onClose}
                      isInGarden={gardenPlantIds.has(companionPlant.id)}
                      onPlantClick={() => handleCompanionClick(companionPlant.id)}
                    />
                  </div>
                );
              })}
              {availableCompanions.length > 3 && (
                <button
                  onClick={handleViewCompanions}
                  className="w-full text-sm text-primary-600 hover:text-primary-700 mt-2"
                >
                  View All Companion Plants
                </button>
              )}
            </div>
          </div>
        ) : null}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={handleGoToGarden}
            className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Go to My Garden
          </button>
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}